import {useCallback, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import {Body1, Body2} from '@ui/atoms/typography';
import {GreenWhitePlusIcon} from '@ui/atoms/icons/greenWhitePlus';
import {useCreateQuestionHook} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins-questions/components/create-questions/create-question.hooks';
import {
  BlockType,
  ChecklistData,
  ContentBlock,
  ContentStepProps,
  PulseBlockData,
  QuestionSource,
  TemplateQuestionOption,
} from './types';
import {
  ContentWrapper,
  ContentSection,
  SectionHeader,
  QuestionList,
  AddOptionsWrapper,
  AddOptionButton,
  AddOptionIcon,
  AddOptionLabel,
  QuestionSourceAnchor,
} from './content-step.styles';
import {SingleQuestionBlock} from './single-question-block';
import {PulseBlock} from './pulse-block';
import {ChecklistBlock} from './checklist-block';
import {GoalsBlock} from './goals-block';
import {QuestionSourceMenu} from './question-source-menu';
import {QuestionTemplateBrowser} from './question-template-browser';
import {
  CHECKLISTS_ENABLED,
  getVisibleContentBlocks,
  isVisibleContentBlock,
} from '../../checklist-availability';
import {
  DEFAULT_PULSE_EMOJIS,
  DEFAULT_PULSE_VALUES,
  PULSE_SCALE,
} from '../../shared/pulse-scale';

const pulseOptions = PULSE_SCALE.map(({key, label}) => ({key, label}));

const defaultPulseData: PulseBlockData = {
  name: 'How are you feeling about your workload this week?',
  options: DEFAULT_PULSE_VALUES,
  emojis: DEFAULT_PULSE_EMOJIS,
  required: true,
  anonymous: false,
};

const createPulseData = (): PulseBlockData => ({
  ...defaultPulseData,
});

const createChecklistData = (): ChecklistData => ({
  title: '',
  checklist: [{text: ''}, {text: ''}],
  required: false,
});

const createBlockId = (type: BlockType) =>
  `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const ContentStep = observer(
  ({
    compact = false,
    blocks,
    onBlocksChange,
    showValidation = false,
    visibility,
    onVisibilityChange,
  }: ContentStepProps) => {
    const {
      questions__,
      loadingQuestionTemplates,
      questionTemplatesError,
      retryQuestionTemplates,
    } = useCreateQuestionHook();
    const [showQuestionSource, setShowQuestionSource] = useState(false);
    const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
    const questionSourceAnchorRef = useRef<HTMLDivElement>(null);
    const addQuestionButtonRef = useRef<HTMLButtonElement>(null);

    const hasPulse = useMemo(
      () => blocks.some((block) => block.type === 'pulse'),
      [blocks],
    );
    const hasGoals = useMemo(
      () => blocks.some((block) => block.type === 'goals'),
      [blocks],
    );

    const visibleBlocks = useMemo(() => getVisibleContentBlocks(blocks), [
      blocks,
    ]);

    const isAnonymous = visibility === 'anonymous';

    const templateQuestions = useMemo<TemplateQuestionOption[]>(
      () =>
        (questions__ || []).flatMap((questionGroup: any) => {
          const category =
            questionGroup?.type?.label || questionGroup?.type?.value || '';
          if (!category || questionGroup?.type?.value === 'custom') {
            return [];
          }
          return (questionGroup?.question || [])
            .map((question: any) => ({
              id: `${category}::${String(question.value)}`,
              label: question.label,
              category,
            }))
            .filter((question: TemplateQuestionOption) => question.label);
        }),
      [questions__],
    );

    const addedTemplateIds = useMemo(
      () =>
        blocks
          .filter((block) => block.type === 'question')
          .map((block) =>
            block.type === 'question' &&
            block.questionData.source === 'template'
              ? block.questionData.questionId
              : undefined,
          )
          .filter(Boolean) as string[],
      [blocks],
    );

    const handleRemoveBlock = (id: string) => {
      onBlocksChange(blocks.filter((block) => block.id !== id));
    };

    const focusBlock = useCallback((id: string) => {
      window.requestAnimationFrame(() => {
        document
          .querySelector<HTMLElement>(`[data-content-block-id="${id}"]`)
          ?.focus();
      });
    }, []);

    const handleAddBlock = (type: Exclude<BlockType, 'question'>) => {
      if (type === 'pulse' && hasPulse) {
        return;
      }
      if ((type === 'checklist' || type === 'goals') && isAnonymous) {
        return;
      }
      if (type === 'goals' && hasGoals) {
        return;
      }

      let newBlock: ContentBlock;
      if (type === 'pulse') {
        newBlock = {
          id: createBlockId('pulse'),
          type: 'pulse',
          pulseData: createPulseData(),
        };
      } else if (type === 'goals') {
        newBlock = {
          id: createBlockId('goals'),
          type: 'goals',
        };
      } else {
        newBlock = {
          id: createBlockId('checklist'),
          type: 'checklist',
          checklistData: createChecklistData(),
        };
      }

      onBlocksChange([...blocks, newBlock]);
    };

    const handleQuestionSourceSelect = (source: QuestionSource) => {
      setShowQuestionSource(false);
      if (source === 'template') {
        setShowTemplateBrowser(true);
        return;
      }

      const id = createBlockId('question');
      onBlocksChange([
        ...blocks,
        {
          id,
          type: 'question',
          questionData: {
            type: 'custom',
            source: 'custom',
            optional: true,
            answerType: 'text',
            text: '',
          },
        },
      ]);
      focusBlock(id);
    };

    const handleAddTemplateQuestions = (
      selectedQuestions: TemplateQuestionOption[],
    ) => {
      const existingIds = new Set(addedTemplateIds);
      const newBlocks: ContentBlock[] = selectedQuestions
        .filter((question) => !existingIds.has(question.id))
        .map((question) => ({
          id: createBlockId('question'),
          type: 'question',
          questionData: {
            type: question.category,
            source: 'template',
            templateCategory: question.category,
            questionId: question.id,
            text: question.label,
            optional: true,
            answerType: 'text',
          },
        }));

      if (newBlocks.length) {
        onBlocksChange([...blocks, ...newBlocks]);
        focusBlock(newBlocks[0].id);
      }
      setShowTemplateBrowser(false);
      addQuestionButtonRef.current?.focus();
    };

    const closeTemplateBrowser = () => {
      setShowTemplateBrowser(false);
      addQuestionButtonRef.current?.focus();
    };

    const handleDragEnd = (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const items = Array.from(visibleBlocks);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      const hiddenBlocks = blocks.filter(
        (block) => !isVisibleContentBlock(block),
      );
      onBlocksChange([...items, ...hiddenBlocks]);
    };

    const renderBlock = (block: ContentBlock, index: number) => {
      switch (block.type) {
        case 'question':
          return (
            <Draggable key={block.id} draggableId={block.id} index={index}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  data-content-block-id={block.id}
                  tabIndex={-1}
                  style={{...provided.draggableProps.style}}>
                  <SingleQuestionBlock
                    compact={compact}
                    data={block.questionData || {optional: true}}
                    onChange={(newData) => {
                      onBlocksChange(
                        blocks.map((b) =>
                          b.id === block.id
                            ? {
                                ...b,
                                type: 'question',
                                questionData: newData,
                              }
                            : b,
                        ),
                      );
                    }}
                    onRemove={() => handleRemoveBlock(block.id)}
                    dragHandleProps={provided.dragHandleProps}
                    showValidation={showValidation}
                  />
                </div>
              )}
            </Draggable>
          );

        case 'pulse':
          return (
            <Draggable key={block.id} draggableId={block.id} index={index}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  data-content-block-id={block.id}
                  tabIndex={-1}
                  style={{
                    ...provided.draggableProps.style,
                  }}>
                  <PulseBlock
                    block={block}
                    blocks={blocks}
                    dragHandleProps={provided.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    pulseOptions={pulseOptions}
                    onBlocksChange={onBlocksChange}
                    onRemove={() => handleRemoveBlock(block.id)}
                    createPulseData={createPulseData}
                  />
                </div>
              )}
            </Draggable>
          );

        case 'checklist':
          return (
            <Draggable key={block.id} draggableId={block.id} index={index}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  data-content-block-id={block.id}
                  tabIndex={-1}
                  style={{
                    ...provided.draggableProps.style,
                  }}>
                  <ChecklistBlock
                    compact={compact}
                    block={block}
                    blocks={blocks}
                    dragHandleProps={provided.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    showValidation={showValidation}
                    onBlocksChange={onBlocksChange}
                    onRemove={() => handleRemoveBlock(block.id)}
                    createChecklistData={createChecklistData}
                    incompatible={isAnonymous}
                  />
                </div>
              )}
            </Draggable>
          );

        case 'goals':
          return (
            <Draggable key={block.id} draggableId={block.id} index={index}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  data-content-block-id={block.id}
                  tabIndex={-1}
                  style={{...provided.draggableProps.style}}>
                  <GoalsBlock
                    dragHandleProps={provided.dragHandleProps}
                    isDragging={snapshot.isDragging}
                    incompatible={isAnonymous}
                    onRemove={() => handleRemoveBlock(block.id)}
                  />
                </div>
              )}
            </Draggable>
          );

        default:
          return null;
      }
    };

    return (
      <ContentWrapper $compact={compact}>
        <section
          className={`flex flex-col rounded-[10px] border border-borderDark bg-white ${
            compact ? 'gap-3 p-3' : 'gap-4 p-4'
          }`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <Body1 className="leading-6" kind="blackTwo" weight="bold">
                Visibility settings
              </Body1>
              <Body2 className="leading-[22px]" kind="textBody">
                Who can see submissions?
              </Body2>
            </div>
            <div
              role="radiogroup"
              aria-label="Visibility settings"
              className="flex w-fit items-center rounded-lg bg-backgroundDark p-1">
              {[
                {label: 'Public', value: 'public' as const},
                {label: 'Private', value: 'private' as const},
                {label: 'Anonymous', value: 'anonymous' as const},
              ].map((option) => {
                const selected = visibility === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onVisibilityChange(option.value)}
                    className={`h-8 rounded-lg px-3 text-sm leading-[22px] focus:outline-none focus:ring-2 focus:ring-purple200 ${
                      selected
                        ? 'bg-white font-medium text-blackTwo shadow-[0px_2px_5px_rgba(172,172,190,0.24)]'
                        : 'font-normal text-textBody'
                    }`}>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          {isAnonymous && (
            <div className="rounded-lg bg-backgroundDark p-2 text-center text-sm leading-[22px] text-textBody">
              Anonymous cycles only support Pulse and Question blocks.
            </div>
          )}
        </section>
        <ContentSection $compact={compact} data-testid="content-section">
          <SectionHeader>
            <Body1 className="leading-6" kind="blackTwo" weight="bold">
              Content
            </Body1>
            <Body2 className="leading-[22px]" kind="textBody">
              Build what participants will see and respond to.
            </Body2>
          </SectionHeader>

          {visibleBlocks.length > 0 && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="blocks">
                {(provided) => (
                  <QuestionList
                    $compact={compact}
                    {...provided.droppableProps}
                    ref={provided.innerRef}>
                    {visibleBlocks.map((block, index) =>
                      renderBlock(block, index),
                    )}
                    {provided.placeholder}
                  </QuestionList>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {visibleBlocks.length === 0 && (
            <div
              className={`flex items-center justify-center rounded-[10px] border border-borderLight bg-white px-6 text-center ${
                compact ? 'min-h-[88px] py-6' : 'min-h-[111px] py-10'
              }`}>
              <Body2 className="leading-[22px]" kind="textBody">
                No content yet. Add a block below
              </Body2>
            </div>
          )}

          <AddOptionsWrapper>
            <QuestionSourceAnchor ref={questionSourceAnchorRef}>
              <AddOptionButton
                ref={addQuestionButtonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={showQuestionSource}
                onClick={() => setShowQuestionSource((open) => !open)}>
                <AddOptionIcon>
                  <GreenWhitePlusIcon />
                </AddOptionIcon>
                <AddOptionLabel kind="textBody">Add question</AddOptionLabel>
              </AddOptionButton>
              <QuestionSourceMenu
                anchorRef={questionSourceAnchorRef}
                open={showQuestionSource}
                onClose={() => {
                  setShowQuestionSource(false);
                  addQuestionButtonRef.current?.focus();
                }}
                onSelect={handleQuestionSourceSelect}
              />
            </QuestionSourceAnchor>

            {CHECKLISTS_ENABLED && (
              <AddOptionButton
                type="button"
                disabled={isAnonymous}
                onClick={() => handleAddBlock('checklist')}>
                <AddOptionIcon disabled={isAnonymous}>
                  <GreenWhitePlusIcon disabled={isAnonymous} />
                </AddOptionIcon>
                <AddOptionLabel disabled={isAnonymous}>
                  Add Checklist
                </AddOptionLabel>
              </AddOptionButton>
            )}

            <AddOptionButton
              type="button"
              disabled={hasPulse}
              onClick={() => handleAddBlock('pulse')}>
              <AddOptionIcon disabled={hasPulse}>
                <GreenWhitePlusIcon disabled={hasPulse} />
              </AddOptionIcon>
              <AddOptionLabel disabled={hasPulse}>Add pulse</AddOptionLabel>
            </AddOptionButton>

            <AddOptionButton
              type="button"
              disabled={hasGoals || isAnonymous}
              onClick={() => handleAddBlock('goals')}>
              <AddOptionIcon disabled={hasGoals || isAnonymous}>
                <GreenWhitePlusIcon disabled={hasGoals || isAnonymous} />
              </AddOptionIcon>
              <AddOptionLabel disabled={hasGoals || isAnonymous}>
                Add goals
              </AddOptionLabel>
            </AddOptionButton>
          </AddOptionsWrapper>
        </ContentSection>
        <QuestionTemplateBrowser
          open={showTemplateBrowser}
          options={templateQuestions}
          loading={loadingQuestionTemplates}
          error={questionTemplatesError}
          alreadyAddedIds={addedTemplateIds}
          onRetry={retryQuestionTemplates}
          onClose={closeTemplateBrowser}
          onConfirm={handleAddTemplateQuestions}
        />
      </ContentWrapper>
    );
  },
);
